import {_, React, observer, observable, computed, IDisposer, action, useLocalStore, useEffect, useLayoutEffect, classNames, useAsObservableSource, autorun, Popover2, Fragment} from '@api'
import {Tooltip} from '@material-ui/core'
import {useVipLocalStore, VipLocalStore} from '@modules'
import {StoresInstance} from '@stores'
import {RowButton} from '~components/RowButton'
import * as css from './StyleMenu.css'
import {Divider, textAlignStyles, ToolButton, useTldrawApp, TldrawApp, WhiteboardShapeType, TDShapeType} from '@modules/whiteboard'
import {
  CircleIcon,
  DashDashedIcon,
  DashDottedIcon,
  DashDrawIcon,
  DashSolidIcon,
  SizeLargeIcon,
  SizeMediumIcon,
  SizeSmallIcon
} from '@modules/whiteboard/components/icons'
import {strokes, ColorStyle, fills, createDefaultStyle, createDefaultTextStyle, DashStyle, ShapeStyles, SizeStyle, FontStyle} from '@modules/whiteboard/shapes/shared/shape-styles'
import {AlignStyle} from '@modules/whiteboard/types'
import {TextAlignCenterIcon, TextAlignJustifyIcon, TextAlignLeftIcon, TextAlignRightIcon } from '@radix-ui/react-icons'

const STYLE_KEYS = Object.keys(createDefaultTextStyle()) as (keyof ShapeStyles)[]

export const validShapesToStyle = new Set<TDShapeType>(['ellipse', 'frame', 'image', 'line', 'rectangle', 'arrow', 'draw', 'highlighter'])
export const validStrokeShapesToStyle = new Set<TDShapeType>(['ellipse', 'line', 'rectangle', 'arrow', 'draw', 'highlighter'])
export const validTextColorShapesToStyle = new Set<TDShapeType>(['frame', 'image', 'text', 'sticky'])
export const validBackgroundColorShapesToStyle = new Set<TDShapeType>(['frame', 'image', 'text', 'sticky', 'ellipse', 'rectangle'])

const DASH_ICONS = {
  [DashStyle.Draw]: <DashDrawIcon/>,
  [DashStyle.Solid]: <DashSolidIcon/>,
  [DashStyle.Dashed]: <DashDashedIcon/>,
  [DashStyle.Dotted]: <DashDottedIcon/>
}

const SIZE_ICONS = {
  [SizeStyle.Small]: <SizeSmallIcon/>,
  [SizeStyle.Medium]: <SizeMediumIcon/>,
  [SizeStyle.Large]: <SizeLargeIcon/>
}

const ALIGN_ICONS = {
  ['start']: <TextAlignLeftIcon/>,
  ['middle']: <TextAlignCenterIcon/>,
  ['end']: <TextAlignRightIcon/>,
  ['justify']: <TextAlignJustifyIcon/>,
}

//
// const themeSelector = (s: TDSnapshot) => (s.settings.isDarkMode ? 'dark' : 'light')
//
// const showTextStylesSelector = (s: TDSnapshot) => {
//   const pageId = s.appState.currentPageId
//   const page = s.document.pages[pageId]
//   return s.document.pageStates[pageId].selectedIds.some((id) => 'text' in page.shapes[id])
// }

/// region Store

export class StyleMenuStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Omit<MyProps, 'className'> & { tldraw: TldrawApp }) {
    super(stores)

    this.displayedStyle = this.ui.currentStyle
  }

  get ui() { return this.store.ui}

  get tldraw() { return this.props.tldraw}
  registerEffects = () => {
    const _toDispose: Array<IDisposer> = [
      autorun(() => {
        const {
          store: {
            ui: {currentStyle},
            page,
            selectedIds
          }
        } = this

        let commonStyle = {} as ShapeStyles

        if (selectedIds.length <= 0) {
          commonStyle = currentStyle
        } else {
          const overrides = new Set<string>([])

          selectedIds
            .map((id) => page.shapes[id])
            .filter(_.identity)
            .forEach((shape) => {
              STYLE_KEYS.forEach((key) => {
                if (!shape.style || overrides.has(key)) return
                if (commonStyle[key] === undefined) {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  commonStyle[key] = shape.style[key]
                } else {
                  if (commonStyle[key] === shape.style[key]) return
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  commonStyle[key] = shape.style[key]
                  overrides.add(key)
                }
              })
            })
        }

        // Until we can work out the correct logic for deciding whether or not to
        // update the selected style, do a string comparison. Yuck!
        if (JSON.stringify(commonStyle) !== JSON.stringify(this.displayedStyle)) {
          this.displayedStyle = commonStyle
        }
      })
    ]

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  get store() { return this.props.tldraw}

  get appState() { return this.store.appState}

  get applyStyle() { return this.store.applyStyle}

  @observable displayedStyle : ShapeStyles

  @action onToggleFilled = () => {
    this.applyStyle({isFilled: !this.displayedStyle.isFilled})
  }

  @action onDashChange = (value: string) => {
    this.applyStyle({dash: value as DashStyle})
  }

  @action onSizeChange = (value: string) => {
    this.applyStyle({size: value as SizeStyle})
  }

  @computed get showTextStyles() {
    const {store: {selectedShapes}} = this
    return selectedShapes.some(s => s.type === 'text' || s.type === 'sticky')
  }

  @action handleFontChange = (value: string) => {
    this.store.applyStyle({font: value as FontStyle})
  }

  @action handleTextAlignChange = (value: string) => {
    this.store.applyStyle({textAlign: value as AlignStyle})
  }

  get app() { return this.props.tldraw}

  @computed get visible() {
    const {props: {inToolbar}} = this
    
    if (!inToolbar) return true

    const {tldraw: {selectedShapes}} = this

    return selectedShapes.length === 0 || selectedShapes.some(s => validShapesToStyle.has(s.type))
  }
}

export const StyleMenuStoreContext = React.createContext<StyleMenuStore>(null)
export const useStyleMenuStore = () => {
  const store = React.useContext(StyleMenuStoreContext),
    tldraw = useTldrawApp(),
    obsProps = useAsObservableSource({tldraw}),
    store2 = useVipLocalStore((stores) => store ?? new StyleMenuStore(stores, obsProps))

  return store2
}

/// endregion

/// region Components

interface MyProps {
  className?: string
  inToolbar?: boolean
}

export const StyleMenu = observer<MyProps, any>(function StyleMenu({className, inToolbar, ...props}, ref) {
  const tldraw = useTldrawApp(),
    obsProps = useAsObservableSource({...props, inToolbar, tldraw}),
    store = useVipLocalStore((stores) => new StyleMenuStore(stores, obsProps)),
    {displayedStyle, store: {theme, isCreating}, showTextStyles, visible} = store

  useEffect(() => store.registerEffects(), [])

  if (!visible) return null

  return (
    <StyleMenuStoreContext.Provider value={store}>
      <Popover2
        ref={ref}
        interactionKind='click'
        position='bottom-right'
        disabled={isCreating}
        content={<StyleMenuPanel/>}
      >
        <Tooltip title={'Style'} placement='bottom'>
          <ToolButton variant='text'>
            <div className={css.overlapIcons} data-color={{}}
               style={{
                color: strokes[theme][displayedStyle.textColor as ColorStyle],
              }}
            >
              {displayedStyle.isFilled && (
                <CircleIcon
                  size={16}
                  stroke="none"
                  fill={fills[theme][displayedStyle.textColor as ColorStyle]}
                />
              )}
              {DASH_ICONS[displayedStyle.dash]}
            </div>
          </ToolButton>
        </Tooltip>
      </Popover2>
    </StyleMenuStoreContext.Provider>
  )
}, {forwardRef: true})

// const StyledRowInner = styled('div', {
//   height: '100%',
//   width: '100%',
//   backgroundColor: '$panel',
//   borderRadius: '$2',
//   display: 'flex',
//   gap: '$1',
//   flexDirection: 'row',
//   alignItems: 'center',
//   padding: '0 $3',
//   justifyContent: 'space-between',
//   border: '1px solid transparent',

//   '& svg': {
//     position: 'relative',
//     stroke: '$overlay',
//     strokeWidth: 1,
//     zIndex: 1,
//   },
// })

export const StyleMenuPanel = observer<{variant?: 'menu'}>(function StyleMenuPanel({variant}) {
  const {displayedStyle, app: {hasSelection}, onDashChange, onSizeChange, onToggleFilled, applyStyle, showTextStyles, handleFontChange, handleTextAlignChange } = useStyleMenuStore()

  return (
    <div className={css.menuPanel} data-variant={variant}>
      <div className={css.row} data-tall>
        <span>Color</span>
        <div className={css.colorGrid}>
          {Object.keys(strokes.light).map((style: string) => (
              <ToolButton
                className={css.item}
                key={style}
                variant='icon'
                isActive={displayedStyle.textColor === style}
                onClick={() => applyStyle({textColor: style as ColorStyle})}
              >
                <CircleIcon
                  size={18}
                  strokeWidth={2.5}
                  fill={
                    displayedStyle.isFilled
                    ? fills.light[style as ColorStyle]
                    : 'transparent'
                  }
                  stroke={strokes.light[style as ColorStyle]}
                />
              </ToolButton>
          ))}
        </div>
      </div>
      <Divider/>
      <RowButton variant='styleMenu' hasIndicator={!!displayedStyle.isFilled} onClick={onToggleFilled}>
        Fill
      </RowButton>
      <Divider/>
      <div className={css.row}>
        Dash
        <div className={css.group} dir='ltr'>
          {Object.values(DashStyle).map((style) => (
            <ToolButton
              key={style}
              variant='icon'
              isActive={style === displayedStyle.dash}
              data-value={style}
              onClick={() => applyStyle({dash: style})}
            >
              {DASH_ICONS[style as DashStyle]}
            </ToolButton>
          ))}
        </div>
      </div>
      <Divider/>
      <div className={css.row}>
        Size
        <div className={css.group} dir='ltr'>
          {Object.values(SizeStyle).map((sizeStyle) => (
            <ToolButton
              key={sizeStyle}
              isActive={sizeStyle === displayedStyle.size}
              data-value={sizeStyle}
              onClick={() => applyStyle({size: sizeStyle})}
            >
              {SIZE_ICONS[sizeStyle as SizeStyle]}
            </ToolButton>
          ))}
        </div>
      </div>
      {showTextStyles && (
        <Fragment>
          <Divider />
          <div className={css.row}>
            Font
            <div className={css.group}>
              {Object.values(FontStyle).map((fontStyle) => (
                <ToolButton
                  key={fontStyle}
                  isActive={fontStyle === displayedStyle.font}
                  data-value={fontStyle}
                  onClick={() => handleFontChange(fontStyle)}
                >
                  <div className={css.fontIcon} data-variant={fontStyle}>Aa</div>
                </ToolButton>
              ))}
            </div>
          </div>
          <Divider/>
          <div className={css.row}>
            Align
            <div className={css.group}>
            {textAlignStyles.map((style) => (
                <ToolButton
                  key={style}
                  isActive={style === displayedStyle.textAlign}
                  data-value={style}
                  onClick={() => handleTextAlignChange(style)}
                >
                  {ALIGN_ICONS[style]}
                </ToolButton>
              ))}
            </div>
          </div>
        </Fragment>
      )}
    </div>
  )
})


// WEBPACK FOOTER //
// ./src/modules/whiteboard/components/TopPanel/StyleMenu/StyleMenu.tsx